/**
 * @file Auth real (productivo) — feature 004 US2 + feature 005 US2 OTP.
 *
 * Contratos:
 *   - specs/004-go-live/contracts/auth-real-contract.md
 *   - specs/005-auth-completion/contracts/auth-otp-contract.md
 *
 * El backend `/auth/login` devuelve dos shapes posibles:
 *
 *   A) Path regular (rol no requiere OTP):
 *      200 + Set-Cookie: omnipos.session
 *      body: { ok: true, session: {...SessionUser} }
 *
 *   B) Path OTP (rol requiere 2FA — superuser):
 *      202 + Set-Cookie: omnipos.challenge (Path=/auth)
 *      body: { ok: true, challenge: true,
 *              challenge_expires_at: "2026-04-27T12:05:00Z",
 *              target: "c***@gmail.com" }
 *
 * El SPA discrimina con el campo `challenge` y muestra el form de OTP
 * cuando es true. Tras `loginVerifyOTP` exitoso recibe el cookie
 * omnipos.session normal.
 */

import { apiBase } from "../../../lib/env";

export interface SessionUser {
  email: string;
  nombre: string;
  rol: string;
  loggedInAt: string;
  /** URL CDN del avatar de perfil. Vacío/undefined cuando el user no
   *  tiene avatar — el render cae a iniciales (feature 007). */
  avatar_url?: string;
  /** Set de permisos atómicos del rol del user (feature 031). Solo
   *  informativo para el SPA: se usa para filtrar el Centro de Ayuda
   *  y futuras vistas dependientes de permiso. El backend sigue
   *  siendo la fuente de verdad — cada endpoint valida el permiso
   *  real por su cuenta. Puede venir undefined si el server no lo
   *  cableó (graceful fallback: se asume conjunto vacío). */
  permissions?: string[];
  /** ISO8601 UTC del horizonte server-side de la sesión
   *  (auth_sessions.expires_at, post-sliding refresh). El SPA lo
   *  usa para dimensionar el inactivity timer: con "Mantener
   *  sesión" marcado este valor está a 24h del login (feature
   *  032 + 034 hotfix). Opcional para compat con backends viejos
   *  — sin él el SPA cae al default 30min. */
  session_expires_at?: string;
}

export interface LoginInput {
  email: string;
  password: string;
  remember: boolean;
}

/**
 * Resultado del POST /auth/login. 3 variantes:
 *   - `success`: rol regular → SessionUser entregado, navegar a /app.
 *   - `challenge`: rol superuser → mostrar OTPVerificationForm.
 *   - `error`: credenciales/red/etc. — mensaje user-facing.
 */
export type LoginResult =
  | { ok: true; session: SessionUser }
  | { ok: true; challenge: true; challengeExpiresAt: string; target: string }
  | { ok: false; error: string };

export interface LoginVerifyOTPResult {
  ok: true;
  session: SessionUser;
}

export interface LoginVerifyOTPError {
  ok: false;
  error: string;
  /** True si el challenge expiró/no existe — el caller debe volver al form de credenciales. */
  expired?: boolean;
  /** True si se agotaron los attempts — el caller debe volver al form de credenciales. */
  exhausted?: boolean;
}

export interface LoginResendOTPResult {
  ok: true;
  challengeExpiresAt: string;
}

export interface LoginResendOTPError {
  ok: false;
  error: string;
  retryAfterSeconds?: number;
  expired?: boolean;
}

export const SESSION_STORAGE_KEY = "omnipos.session.v1"; // legacy
export const ERROR_INVALID_CREDENTIALS =
  "Credenciales inválidas. Verifica tu email y contraseña.";
export const ERROR_NETWORK =
  "No pudimos conectar con el servidor. Intenta de nuevo.";
export const ERROR_OTP_INVALID =
  "Código incorrecto. Verifica e intenta de nuevo.";
export const ERROR_OTP_EXPIRED =
  "El código expiró. Vuelve a iniciar sesión.";
export const ERROR_OTP_EXHAUSTED =
  "Demasiados intentos. Vuelve a iniciar sesión.";

/**
 * Login productivo: POST a /auth/login del backend `_health`.
 *
 * Discrimina entre regular (200 + session) y OTP-required (202 +
 * challenge). En ambos casos el server emite la cookie HttpOnly
 * correspondiente; el SPA solo lee el body para decidir UI.
 */
export async function login(input: LoginInput): Promise<LoginResult> {
  try {
    const res = await fetch(`${apiBase()}/auth/login`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const json = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      session?: SessionUser;
      challenge?: boolean;
      challenge_expires_at?: string;
      target?: string;
      error?: string;
    };

    if (!res.ok) {
      return { ok: false, error: json.error ?? ERROR_INVALID_CREDENTIALS };
    }

    if (json.challenge === true) {
      return {
        ok: true,
        challenge: true,
        challengeExpiresAt: json.challenge_expires_at ?? "",
        target: json.target ?? input.email,
      };
    }

    if (!json.session) {
      return { ok: false, error: ERROR_NETWORK };
    }
    return { ok: true, session: json.session };
  } catch {
    return { ok: false, error: ERROR_NETWORK };
  }
}

/**
 * POST /auth/otp/verify — valida el código del challenge activo.
 * El browser envía la cookie omnipos.challenge automáticamente
 * por `credentials: include`.
 *
 * En éxito, el server emite Set-Cookie: omnipos.session + clear
 * omnipos.challenge.
 */
export async function loginVerifyOTP(
  code: string,
): Promise<LoginVerifyOTPResult | LoginVerifyOTPError> {
  try {
    const res = await fetch(`${apiBase()}/auth/otp/verify`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    });
    const json = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      session?: SessionUser;
      error?: string;
    };

    if (res.status === 410) {
      return {
        ok: false,
        error: json.error ?? ERROR_OTP_EXPIRED,
        expired: true,
      };
    }
    if (res.status === 401) {
      // Distinguir exhausted vs invalid por el mensaje del server.
      const msg = json.error ?? ERROR_OTP_INVALID;
      const exhausted = /Demasiados intentos/i.test(msg);
      return { ok: false, error: msg, exhausted };
    }
    if (!res.ok || !json.session) {
      return { ok: false, error: json.error ?? ERROR_NETWORK };
    }
    return { ok: true, session: json.session };
  } catch {
    return { ok: false, error: ERROR_NETWORK };
  }
}

/**
 * POST /auth/otp/resend — refresh del código en el challenge activo.
 * Rate-limited (1/60s + 5/h).
 */
export async function loginResendOTP(): Promise<
  LoginResendOTPResult | LoginResendOTPError
> {
  try {
    const res = await fetch(`${apiBase()}/auth/otp/resend`, {
      method: "POST",
      credentials: "include",
    });
    const json = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      challenge_expires_at?: string;
      error?: string;
    };
    if (res.status === 429) {
      const retryAfter = res.headers.get("Retry-After");
      return {
        ok: false,
        error: json.error ?? "Demasiados reenvíos. Espera un minuto.",
        retryAfterSeconds: retryAfter ? Number(retryAfter) : 60,
      };
    }
    if (res.status === 410) {
      return {
        ok: false,
        error: json.error ?? ERROR_OTP_EXPIRED,
        expired: true,
      };
    }
    if (!res.ok || !json.challenge_expires_at) {
      return { ok: false, error: json.error ?? ERROR_NETWORK };
    }
    return { ok: true, challengeExpiresAt: json.challenge_expires_at };
  } catch {
    return { ok: false, error: ERROR_NETWORK };
  }
}

/**
 * Lee la sesión activa via GET /auth/me (browser envía la cookie
 * automáticamente por `credentials: 'include'`).
 *
 * Dedup + cache TTL (perf 2026-04-30):
 *   - Si hay una request en vuelo, los callers concurrentes reusan
 *     la misma Promise (evita N llamadas duplicadas cuando
 *     RequireAuth + DashboardLayout + DashboardHome montan a la vez).
 *   - El último resultado positivo se cachea por GET_SESSION_TTL_MS;
 *     refrescar la página dentro de ese TTL no re-fetcha.
 *   - `clearSession()` y la falla del fetch (null) invalidan el
 *     cache para forzar re-check en la siguiente llamada.
 */
const GET_SESSION_TTL_MS = 30 * 1000; // 30s — short, suficiente
                                       // para coalescer mounts paralelos
                                       // sin enmascarar revocaciones.
/**
 * Resultado tipado del check de sesión. Distingue el caso "no
 * autenticado" (401/403 — cookie ausente/inválida → redirigir a login)
 * del caso "error transitorio" (cold-start de Neon, blip de red →
 * reintentable). `getSession()` colapsa esto a `SessionUser | null` para
 * mantener compatibilidad con los callers existentes; las vistas que
 * quieran mostrar un estado "Reintentar" usan `getSessionOutcome()`.
 */
export type SessionOutcome =
  | { status: "authenticated"; user: SessionUser }
  | { status: "unauthenticated" }
  | { status: "error" };

// Reintentos ante fallos TRANSITORIOS del GET /auth/me (cold-start del
// branch Neon que autosuspende a los 60s, o blip de red). NO se reintenta
// en 401/403 (sesión inválida real). Sin esto, un único fallo dejaba la
// sesión en null → RequireAuth rebotaba a /login y el dashboard se vaciaba
// sin aviso. Backoff corto: 3 intentos en ~1.2s peor caso.
const SESSION_RETRY_DELAYS_MS = [300, 900];

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Un intento. Devuelve un SessionOutcome terminal, o "retry" si fue un
// fallo transitorio que amerita reintento.
async function fetchSessionOnce(): Promise<SessionOutcome | "retry"> {
  try {
    const res = await fetch(`${apiBase()}/auth/me`, { credentials: "include" });
    if (res.status === 401 || res.status === 403) {
      return { status: "unauthenticated" };
    }
    if (!res.ok) {
      // 5xx / 502 / 503 / 504 / etc. → transitorio (servidor o DB no listos).
      return "retry";
    }
    const user = (await res.json()) as SessionUser;
    return { status: "authenticated", user };
  } catch {
    // Error de red / fetch abortado → transitorio.
    return "retry";
  }
}

async function fetchSessionWithRetry(): Promise<SessionOutcome> {
  for (let attempt = 0; ; attempt++) {
    const r = await fetchSessionOnce();
    if (r !== "retry") return r;
    if (attempt >= SESSION_RETRY_DELAYS_MS.length) return { status: "error" };
    await sleep(SESSION_RETRY_DELAYS_MS[attempt] ?? 0);
  }
}

let getSessionInFlight: Promise<SessionOutcome> | null = null;
// Solo se cachean resultados POSITIVOS — un error/unauth siempre re-checkea.
let getSessionCache: { user: SessionUser; at: number } | null = null;

/**
 * Check de sesión con reintentos + cache TTL + dedup de requests en
 * vuelo. Resultado tipado (authenticated / unauthenticated / error).
 */
export async function getSessionOutcome(): Promise<SessionOutcome> {
  const now = Date.now();
  if (getSessionCache !== null && now - getSessionCache.at < GET_SESSION_TTL_MS) {
    return { status: "authenticated", user: getSessionCache.user };
  }
  if (getSessionInFlight !== null) {
    return getSessionInFlight;
  }
  getSessionInFlight = (async () => {
    try {
      const outcome = await fetchSessionWithRetry();
      getSessionCache =
        outcome.status === "authenticated"
          ? { user: outcome.user, at: Date.now() }
          : null;
      return outcome;
    } finally {
      getSessionInFlight = null;
    }
  })();
  return getSessionInFlight;
}

export async function getSession(): Promise<SessionUser | null> {
  const outcome = await getSessionOutcome();
  return outcome.status === "authenticated" ? outcome.user : null;
}

/** Invalida el cache de getSession — llamar al logout o al detectar 401. */
export function invalidateSessionCache(): void {
  getSessionCache = null;
  getSessionInFlight = null;
}

/**
 * No-op en feature 004 — la cookie la pone el server.
 * @deprecated Conservado por API stability con feature 003.
 */
export function setSession(_user: SessionUser): boolean {
  return true;
}

/**
 * Logout productivo: POST /auth/logout. El server revoca la sesión
 * y limpia la cookie via `Set-Cookie: ...; Max-Age=-1`.
 */
export async function clearSession(): Promise<void> {
  invalidateSessionCache();
  try {
    await fetch(`${apiBase()}/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    // best-effort
  }
}

/**
 * Conservado por API stability. En feature 004 ya no usamos
 * sessionStorage para la sesión.
 */
export function isSessionStorageAvailable(): boolean {
  return typeof window !== "undefined" && typeof window.fetch === "function";
}

// ════════════════════════════════════════════════════════════════════
// Password reset (feature 013) — proxy a Better Auth /forget-password
// + /reset-password vía nuestro backend (que agrega HIBP + audit).
// ════════════════════════════════════════════════════════════════════

export interface PasswordResetRequestResult {
  ok: boolean;
  /** Mensaje genérico, idéntico tanto si el email existe como si no
   *  (defensa contra user enumeration). */
  message: string;
}

export async function requestPasswordReset(
  email: string,
): Promise<PasswordResetRequestResult> {
  try {
    const res = await fetch(`${apiBase()}/auth/password-reset/request`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    const json = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      message?: string;
    };
    return {
      ok: json.ok === true,
      message:
        json.message ??
        "Si la cuenta existe, te enviamos un email con instrucciones.",
    };
  } catch {
    // Mismo mensaje genérico en error de red — no revelamos nada.
    return {
      ok: true,
      message:
        "Si la cuenta existe, te enviamos un email con instrucciones.",
    };
  }
}

export type PasswordResetConfirmResult =
  | { ok: true; message: string }
  | { ok: false; code: string; message: string };

export async function confirmPasswordReset(
  token: string,
  newPassword: string,
): Promise<PasswordResetConfirmResult> {
  try {
    const res = await fetch(`${apiBase()}/auth/password-reset/confirm`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, newPassword }),
    });
    const json = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      code?: string;
      message?: string;
    };
    if (res.ok && json.ok === true) {
      return {
        ok: true,
        message: json.message ?? "Contraseña actualizada.",
      };
    }
    return {
      ok: false,
      code: json.code ?? "reset_failed",
      message:
        json.message ??
        "No pudimos restablecer tu contraseña. Solicita un link nuevo.",
    };
  } catch {
    return {
      ok: false,
      code: "network",
      message: "No pudimos conectar con el servidor.",
    };
  }
}
